import { add } from "date-fns";
import _sortBy from "lodash/sortBy";
import getConfig from "next/config";

import DeliveryIcon from "@components/Icons/DeliveryIcon";
import LockerIcon from "@components/Icons/Lockers/LockerIcon";
import StoreIcon from "@components/Icons/Store";
import { formatPrice } from "@lib/helpers";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";
const deliveryPointClass = isBricoStore
  ? "text-orange capitalize"
  : "text-[#1D4ED8] capitalize";
const deliveryEstimateClass = isBricoStore ? "text-green" : "text-[#16A34A]";

const deliveryMethodInfo = {
  home: { icon: <DeliveryIcon />, name: "Livrare" },
  locker: { icon: <LockerIcon />, name: "Locker" },
  ship_to_store: { icon: <StoreIcon />, name: "La magazin" },
};

const formatShippingMonth = (date) =>
  date
    .toLocaleString("ro-RO", { month: "short" })
    .replace(".", "")
    .replace(/^./, (c) => c.toUpperCase());

const formatShippingDate = (date) =>
  date
    .toLocaleDateString("ro-RO", {
      month: "long",
      day: "numeric",
    })
    .replace(/\.$/, "")
    .replace(/\b[a-z]/, (c) => c.toUpperCase());

const getShippingEstimateDays = (shippingOption) => {
  const { estimated_delivery_days, estimated_delivery } = shippingOption;

  if (estimated_delivery_days && estimated_delivery_days.toString() === "1") {
    return "Maine";
  } else if (Number(estimated_delivery_days) > 1) {
    const today = new Date();
    const shippingDate = add(today, { days: Number(estimated_delivery_days) });
    return formatShippingDate(shippingDate);
  }

  if (estimated_delivery && estimated_delivery.length) {
    const [minDays, maxDays] = estimated_delivery.split("-").map(Number);

    const today = new Date();

    const d1 = add(today, { days: minDays });
    const d2 = add(today, { days: maxDays });

    const day1 = d1.getDate();
    const day2 = d2.getDate();

    const month1 = formatShippingMonth(d1);
    const month2 = formatShippingMonth(d2);

    if (month1 === month2) {
      return `${day1}-${day2} ${month1}`;
    }

    return `${day1} ${month1} - ${day2} ${month2}`;
  }
};

// The locker fee is flat, so "de la" would wrongly suggest the price can still grow
const getShippingCostLabel = (deliveryMode, estimatedTax) =>
  deliveryMode === "locker"
    ? `- ${formatPrice(estimatedTax)} lei`
    : `- de la ${formatPrice(estimatedTax)} lei`;

const ProductPageShipping = ({ shippingInfo, hasFreeShippingPromo }) => (
  <ul className="space-y-[4px]">
    {_sortBy(shippingInfo, "estimated_tax").map(
      ({ delivery_mode, delivery_point, estimated_tax, carrier, ...rest }) => {
        if (!deliveryMethodInfo[delivery_mode]) {
          return null;
        }

        const normalizedDeliveryPointName =
          delivery_point?.name && delivery_point.name.toLowerCase();

        const shippingEstimateLabel = getShippingEstimateDays(rest);
        const isNextDay = shippingEstimateLabel === "Maine";
        const isFreeShipping =
          Number(estimated_tax) === 0 || hasFreeShippingPromo;
        const isALD = carrier?.toLowerCase() === "altex";

        return (
          <li
            key={delivery_mode}
            className="flex items-center space-x-2 text-[14px]"
          >
            <div
              className={`${
                isBricoStore ? "bg-orange-light text-ironBlack" : "bg-[#F0FDF4]"
              } p-[4px] rounded-full max-w-fit`}
            >
              {deliveryMethodInfo[delivery_mode].icon}
            </div>

            <div className="text-[#737373]">
              {isALD
                ? "Livrare estimata"
                : deliveryMethodInfo[delivery_mode].name}
              : {/* next day with free shipping */}
              {isNextDay && isFreeShipping && (
                <span className="font-medium text-black">
                  <span className={deliveryEstimateClass}>
                    {shippingEstimateLabel}
                  </span>{" "}
                  {normalizedDeliveryPointName && (
                    <>
                      in{" "}
                      <span className={deliveryPointClass}>
                        {normalizedDeliveryPointName}{" "}
                      </span>
                      -
                    </>
                  )}{" "}
                  <span className={deliveryEstimateClass}>gratuit</span>
                </span>
              )}
              {/* next day with paid shipping */}
              {isNextDay && !isFreeShipping && (
                <span className="font-medium text-black">
                  <span className={deliveryEstimateClass}>
                    {shippingEstimateLabel}
                  </span>{" "}
                  {normalizedDeliveryPointName && (
                    <>
                      in{" "}
                      <span className={deliveryPointClass}>
                        {normalizedDeliveryPointName}
                      </span>
                    </>
                  )}{" "}
                  {estimated_tax && (
                    <>{getShippingCostLabel(delivery_mode, estimated_tax)}</>
                  )}
                </span>
              )}
              {/* delivery range or fixed day with free shipping */}
              {!isNextDay && isFreeShipping && (
                <span className="font-medium text-black">
                  {shippingEstimateLabel}{" "}
                  {normalizedDeliveryPointName && (
                    <>
                      in{" "}
                      <span className={deliveryPointClass}>
                        {normalizedDeliveryPointName} -
                      </span>
                    </>
                  )}{" "}
                  <span className={deliveryEstimateClass}>gratuit</span>
                </span>
              )}
              {/* delivery range or fixed day with paid shipping */}
              {!isNextDay && !isFreeShipping && (
                <span className="font-medium text-black">
                  {shippingEstimateLabel}{" "}
                  {normalizedDeliveryPointName && (
                    <>
                      in{" "}
                      <span className={deliveryPointClass}>
                        {normalizedDeliveryPointName}
                      </span>
                    </>
                  )}{" "}
                  {estimated_tax && (
                    <>{getShippingCostLabel(delivery_mode, estimated_tax)}</>
                  )}
                </span>
              )}
            </div>
          </li>
        );
      }
    )}
  </ul>
);

export default ProductPageShipping;
